import React, { useCallback, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../../../../../../store/hooks'
import { selectAccountId } from '../../../../../../store/auth/authSlice'
import { usePollingEffect } from '../../../common/hooks/usePollingEffect'
import { AIAgentType } from '../../../../../../models/AIAgent'
import { useLazyGetAiAgentResultQuery } from '../../../../../../store/ai/aiAgentResult'
import {
  selectMeetingSummaryAgentAiAgentId,
  selectMeetingSummaryAgentPollingEnabled,
  setMeetingSummary,
  setMeetingSummaryAgentEventsForPolling,
  setMeetingSummaryAiAgentStatus,
} from '../../../../../../store/meetingSummaryAgent/meetingSummaryAgent'
import { selectSelectedMeetingTemplate } from '../../../../../../store/meetingSummary/meetingTemplates'
import { useLazyListRoomEventsQuery } from '../../../../../../store/media/media'
import { useLazyGetAiAgentQuery } from '../../../../../../store/ai/aiAgentModel'
import { setMessages } from '../../../../../../store/streamingChat/streamingChat'

export const MeetingSummaryAgentPoller: React.FC = () => {
  const { roomId = '', sessionId = '' } = useParams()
  const accountId = useAppSelector(selectAccountId)
  const selectedMeetingTemplate = useAppSelector(selectSelectedMeetingTemplate)

  usePollMeetingSummaryAgentResult(accountId, roomId, sessionId, selectedMeetingTemplate?.id)
  usePollMeetingSummaryAgentEvents(accountId, roomId, sessionId)
  usePollMeetingSummaryAgentStatus(accountId)

  return null
}

const usePollMeetingSummaryAgentResult = (accountId: string, roomId: string, sessionId: string, meetingTemplateId?: string) => {
  const dispatch = useAppDispatch()

  const meetingSummaryAgentPollingEnabled = useAppSelector(selectMeetingSummaryAgentPollingEnabled)

  const [triggerGetAiAgentResult] = useLazyGetAiAgentResultQuery()

  const getExistingMeetingSummary = useCallback(async () => {
    if (!meetingTemplateId) {
      return
    }
    const existingMeetingSummary = await triggerGetAiAgentResult(
      {
        accountId,
        type: AIAgentType.MEETING_SUMMARY,
        resultKey: `${roomId}|${sessionId}|${meetingTemplateId}`,
      },
      false,
    ).unwrap()
    if (existingMeetingSummary) {
      dispatch(setMeetingSummary(existingMeetingSummary))
      const { interaction } = existingMeetingSummary
      if (!interaction) {
        return
      }
      dispatch(
        setMessages(
          interaction.interactions.map((interaction) => {
            return { message: interaction.result.message, type: interaction.result.role }
          }),
        ),
      )
    }
  }, [dispatch, accountId, roomId, sessionId, meetingTemplateId, triggerGetAiAgentResult])

  const { start: startMeetingSummaryAgentPolling, stop: stopMeetingSummaryAgentPolling } = usePollingEffect(getExistingMeetingSummary, [], {
    interval: 5000,
  })

  useEffect(() => {
    if (meetingSummaryAgentPollingEnabled) {
      startMeetingSummaryAgentPolling()
    } else {
      stopMeetingSummaryAgentPolling()
    }
  }, [meetingSummaryAgentPollingEnabled, startMeetingSummaryAgentPolling, stopMeetingSummaryAgentPolling])
}

const usePollMeetingSummaryAgentEvents = (accountId: string, roomId: string, sessionId: string) => {
  const dispatch = useAppDispatch()

  const [triggerGetRoomEvents] = useLazyListRoomEventsQuery()

  const meetingSummaryAgentPollingEnabled = useAppSelector(selectMeetingSummaryAgentPollingEnabled)

  const getMeetingSummaryAgentEvents = useCallback(async () => {
    const { data: roomEvents } = await triggerGetRoomEvents({
      accountId,
      roomId,
      sessionId,
      pageRequest: { page: 0, size: 500 },
      types: ['aiFeatureExecutionFinished', 'userAllocationUpdated', 'meetingSummaryAgentExecuted'],
    })
    if (!roomEvents?.data) {
      return
    }
    dispatch(setMeetingSummaryAgentEventsForPolling(roomEvents.data))
  }, [triggerGetRoomEvents, accountId, roomId, sessionId, dispatch])

  const { start: startMeetingSummaryAgentEventsPolling, stop: stopMeetingSummaryAgentEventsPolling } = usePollingEffect(getMeetingSummaryAgentEvents, [], {
    interval: 5000,
  })

  useEffect(() => {
    if (meetingSummaryAgentPollingEnabled) {
      startMeetingSummaryAgentEventsPolling()
    } else {
      stopMeetingSummaryAgentEventsPolling()
    }
  }, [meetingSummaryAgentPollingEnabled, startMeetingSummaryAgentEventsPolling, stopMeetingSummaryAgentEventsPolling])
}

const usePollMeetingSummaryAgentStatus = (accountId: string) => {
  const dispatch = useAppDispatch()

  const meetingSummaryAgentId = useAppSelector(selectMeetingSummaryAgentAiAgentId)
  const meetingSummaryAgentPollingEnabled = useAppSelector(selectMeetingSummaryAgentPollingEnabled)

  const [triggerGetAiAgent] = useLazyGetAiAgentQuery()

  const getMeetingSummaryAgentStatus = useCallback(async () => {
    if (!meetingSummaryAgentId) {
      return
    }
    const aiAgent = await triggerGetAiAgent({
      accountId,
      aiAgentId: meetingSummaryAgentId,
    }).unwrap()
    if (!aiAgent) {
      return
    }
    dispatch(setMeetingSummaryAiAgentStatus(aiAgent.status))
  }, [dispatch, accountId, meetingSummaryAgentId, triggerGetAiAgent])

  const { start: startMeetingSummaryAgentStatusPolling, stop: stopMeetingSummaryAgentStatusPolling } = usePollingEffect(getMeetingSummaryAgentStatus, [], {
    interval: 5000,
  })

  useEffect(() => {
    if (meetingSummaryAgentPollingEnabled) {
      startMeetingSummaryAgentStatusPolling()
    } else {
      stopMeetingSummaryAgentStatusPolling()
    }
  }, [meetingSummaryAgentPollingEnabled, startMeetingSummaryAgentStatusPolling, stopMeetingSummaryAgentStatusPolling])
}
